import { Outlet } from 'react-router-dom';

import { BaseMenuDto } from '@sbv-clients/data-access-api';
import { useSSoUserMeta } from '@sbv-clients/shared-feature-auth';
import {
  Navbar,
  Sidebar,
  useBreadcrumbLabelsBuilder,
} from '@sbv-clients/shared-feature-layouts';
import {
  Box,
  CssBaseline,
  GlobalStyles,
  Icon,
  styled,
  useToggleModal,
} from '@sbv-clients/ui-shared';

import { MobileAppVersionDialog } from '../components/mobile-app-version-dialog';

const Root = styled(Box)`
  display: flex;
  height: 100%;
  overflow: hidden;
`;

const drawerWidth = 258;
const Drawer = styled('div')`
  ${(props) => props.theme.breakpoints.up('md')} {
    width: ${drawerWidth}px;
    flex-shrink: 0;
  }
`;

const StyledPageContent = styled(Box)`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 100%;
  overflow: hidden;
  background-color: ${(props) => props.theme.background.white};
  border: 1px solid ${(props) => props.theme.colors.secondary.lighter};
`;

const SIDE_BAR_ITEMS: (BaseMenuDto & { children?: BaseMenuDto[] })[] = [
  { id: '0', label: 'Dashboard', link: '/dashboard' },
  { id: '1', label: 'Databases', link: '/facility' },
  { id: '2', label: 'Database Stats', link: '/facilities-stats' },
  { id: '3', label: 'Base Facility Menu', link: '/base-facility-menu' },
  { id: '4', label: 'Impersonation Logs', link: '/impersonation-logs' },
  { id: '5', label: 'Global Users', link: '/global-users' },
];

const PageContent = ({ onToggleDrawer }: { onToggleDrawer: () => void }) => {
  const { name } = useSSoUserMeta();
  const { breadCrumbLabels } = useBreadcrumbLabelsBuilder(SIDE_BAR_ITEMS);
  const mobileAppVersionModal = useToggleModal();
  return (
    <StyledPageContent m={2} ml={0} borderRadius={1}>
      <Navbar
        onToggleDrawer={onToggleDrawer}
        ssoUserName={name}
        breadCrumbLabels={breadCrumbLabels}
        extraMenuItems={[
          {
            label: 'Mobile App Version',
            icon: <Icon name="PhoneIphoneOutlined" />,
            onClick: mobileAppVersionModal.openModal,
          },
        ]}
      />
      <Box flex={1} overflow="hidden">
        <Outlet />
      </Box>
      <MobileAppVersionDialog
        open={mobileAppVersionModal.open}
        onClose={mobileAppVersionModal.closeModal}
      />
    </StyledPageContent>
  );
};

const PrivateRouteLayout = () => {
  const { open, closeModal, toggleModal } = useToggleModal();
  return (
    <Root>
      <CssBaseline />
      <GlobalStyles />
      <Drawer>
        <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
          <Sidebar
            title="Admin Portal"
            PaperProps={{ style: { width: drawerWidth } }}
            variant="temporary"
            open={open}
            onClose={closeModal}
            items={SIDE_BAR_ITEMS}
          />
        </Box>
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Sidebar
            title="Admin Portal"
            PaperProps={{ style: { width: drawerWidth } }}
            items={SIDE_BAR_ITEMS}
          />
        </Box>
      </Drawer>
      <PageContent onToggleDrawer={toggleModal} />
    </Root>
  );
};

export default PrivateRouteLayout;
